import { useState, useCallback, useEffect } from "react";

/**
 * Custom hook for managing search-related state
 * Consolidates search states and debouncing logic
 */
export const useSearchState = () => {
  // Search states
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Search handlers
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
    setShowSearchResults(true);
  }, []);

  const handleEmployeeSelect = useCallback((employee) => {
    setSelectedEmployee(employee);
    setSearchTerm("");
    setShowSearchResults(false);
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedEmployee(null);
    setSearchTerm("");
    setShowSearchResults(false);
  }, []);

  const handleSearchFocus = useCallback(() => {
    setShowSearchResults(true);
  }, []);

  const handleSearchBlur = useCallback(() => {
    // Delay hiding to allow clicks on results
    setTimeout(() => setShowSearchResults(false), 200);
  }, []);

  const resetSearch = useCallback(() => {
    setSearchTerm("");
    setSelectedEmployee(null);
    setShowSearchResults(false);
    setDebouncedSearchTerm("");
  }, []);

  return {
    // Search states
    searchTerm,
    selectedEmployee,
    showSearchResults,
    debouncedSearchTerm,

    // Search handlers
    handleSearchChange,
    handleEmployeeSelect,
    handleClearSelection,
    handleSearchFocus,
    handleSearchBlur,
    resetSearch,

    // Setters for direct control
    setSearchTerm,
    setSelectedEmployee,
    setShowSearchResults,
    setDebouncedSearchTerm,
  };
};
